import { jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useRef } from 'react';
// Stripe permits exactly ONE embedded Checkout object per page. React StrictMode
// double-invokes effects in dev (mount -> unmount -> mount), which would otherwise
// create two objects in parallel and trigger "You cannot have multiple Embedded
// Checkout objects." We serialize every create/destroy on a single module-level
// promise chain so they can never overlap, and skip any run whose component already
// unmounted.
let currentCheckout = null;
let queue = Promise.resolve();
function destroyCurrent() {
    if (currentCheckout) {
        try {
            currentCheckout.destroy();
        }
        catch (_a) {
            /* already destroyed */
        }
        currentCheckout = null;
    }
}
/**
 * Mounts Stripe's embedded Checkout in-app (no redirect). Stripe is loaded via the
 * js.stripe.com script tag (window.Stripe), the same way EmbeddedPaymentForm loads it.
 * The session is created server-side; here we render Stripe's hosted form and fire
 * onComplete when the payment finishes.
 */
export const StripeEmbeddedCheckout = ({ clientSecret, onComplete, onError, }) => {
    const containerRef = useRef(null);
    const onCompleteRef = useRef(onComplete);
    const onErrorRef = useRef(onError);
    onCompleteRef.current = onComplete;
    onErrorRef.current = onError;
    useEffect(() => {
        let active = true;
        queue = queue.then(async () => {
            var _a, _b, _c;
            // Skip if the component was unmounted before this run reached the front of the
            // queue (the StrictMode double-mount case).
            if (!active)
                return;
            destroyCurrent();
            try {
                // @ts-ignore - Stripe loaded via script tag
                const stripe = (_a = window.Stripe) === null || _a === void 0 ? void 0 : _a.call(window, REACT_APP_STRIPE_PUBLISHABLE_KEY);
                if (!stripe) {
                    (_b = onErrorRef.current) === null || _b === void 0 ? void 0 : _b.call(onErrorRef, 'Payment system not available. Please refresh and try again.');
                    return;
                }
                const checkout = await stripe.initEmbeddedCheckout({
                    fetchClientSecret: async () => clientSecret,
                    onComplete: () => onCompleteRef.current(),
                });
                if (!active) {
                    try {
                        checkout.destroy();
                    }
                    catch (_d) {
                        /* */
                    }
                    return;
                }
                currentCheckout = checkout;
                if (containerRef.current) {
                    checkout.mount(containerRef.current);
                }
            }
            catch (err) {
                (_c = onErrorRef.current) === null || _c === void 0 ? void 0 : _c.call(onErrorRef, err instanceof Error ? err.message : 'Failed to load checkout.');
            }
        });
        return () => {
            active = false;
            queue = queue.then(() => {
                destroyCurrent();
            });
        };
    }, [clientSecret]);
    // Stripe's embedded form is a white, cross-origin iframe we can't theme. Seat
    // it in a deliberately white, rounded, clipped card so its corners blend into
    // this panel instead of leaving square white blips on the sand body.
    return (_jsx("div", { className: "w-full rounded-xl overflow-hidden", style: { backgroundColor: '#ffffff', boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)' }, children: _jsx("div", { ref: containerRef, className: "w-full" }) }));
};
